import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZW5naW5lZXJpbmclMjBtYWludGVuYW5jZXxlbnwwfHx8fDE3ODQ2MzYzNzd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.needs_password_change ? "/passwort-setzen" : "/");
    } catch (err) {
      const status = err.response?.status;
      let msg = formatApiError(err.response?.data?.detail);
      if (!msg) {
        if (!err.response) msg = "Server nicht erreichbar. Bitte in ein paar Sekunden erneut versuchen.";
        else if (status >= 500) msg = `Server vorübergehend nicht verfügbar (${status}). Bitte erneut versuchen.`;
        else if (status === 401) msg = "Ungültige Zugangsdaten.";
        else msg = err.message || "Anmeldung fehlgeschlagen.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <div className="mx-auto w-full max-w-sm animate-fade-in">
          <div className="mb-10 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-black">C</div>
            <div>
              <div className="font-heading text-lg font-black tracking-tight">CELENTANO</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">Service Platform</div>
            </div>
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">Anmelden</h1>
          <p className="mt-2 text-sm text-muted-foreground">Digitalisierte Service-, Wartungs- und Prüfprozesse.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">E-Mail</Label>
              <Input id="email" type="email" data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Passwort</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="h-12" />
            </div>
            {error && <p data-testid="login-error" className="text-sm font-medium text-danger">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="group h-12 w-full gap-2 text-base font-semibold">
              {loading ? "Anmeldung…" : "Anmelden"}
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Button>
            <div className="text-center">
              <Link to="/passwort-vergessen" data-testid="login-forgot-password-link"
                className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                Passwort vergessen?
              </Link>
            </div>
          </form>
        </div>
      </div>
      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={BG} alt="Industrieanlage" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/55 to-black/25" />
        <div className="absolute bottom-14 left-14 max-w-md text-white">
          <p className="font-heading text-3xl font-black leading-tight tracking-tight">
            Apple-Bedienung. Mercedes-Qualität. Hörmann-Zuverlässigkeit.
          </p>
          <p className="mt-4 text-sm text-white/70">
            Offline-first Plattform für Anlagen, Wartung, Prüfungen, Reparaturen und Notdienst.
          </p>
        </div>
      </div>
    </div>
  );
}
